<script setup>
import WebLayout from '@/layouts/Web/Layout.vue';
import { ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import SEOHead from '@/components/SEOHead.vue';

const page = usePage();

const props = defineProps({
    success: String,
    errors: Object
});

const form = ref({
    name: '',
    email: '',
    subject: '',
    message: ''
});

const isSubmitting = ref(false);

const submitForm = async () => {
    isSubmitting.value = true;
    
    try {
        await router.post(route('web.contact.submit'), form.value, {
            onSuccess: () => {
                form.value = { name: '', email: '', subject: '', message: '' };
            },
            onError: (errors) => {
                console.error('Form submission errors:', errors);
            }
        });
    } catch (error) {
        console.error('Form submission error:', error);
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<template>
    <WebLayout>
        <SEOHead
            title="Kontakt - Lexo"
            description="Kontaktirajte nas za vprašanja o pravnih storitvah, podporo ali sodelovanje. Naš tim je na voljo za pomoč pri iskanju pravilnega strokovnjaka za vaše potrebe."
            keywords="kontakt, Lexo, pravne storitve, podpora, pomoč, sodelovanje"
            canonical="/contact"
            image="/images/web/lexo-home.png"
            :structured-data="{
                '@type': 'ContactPage',
                'name': 'Kontakt - Lexo',
                'description': 'Kontaktirajte nas za vprašanja o pravnih storitvah',
                'url': 'https://lexo.si/contact',
                'mainEntity': {
                    '@type': 'Organization',
                    'name': 'Lexo',
                    'url': 'https://lexo.si',
                    'contactPoint': {
                        '@type': 'ContactPoint',
                        'contactType': 'customer service',
                        'email': 'info@lexo.si'
                    }
                }
            }"
        />
        
        <div class="container mx-auto px-4 py-12">
            <div class="max-w-4xl mx-auto">
                <h1 class="text-4xl font-bold text-gray-900 mb-8">Kontakt</h1>
                
                <!-- Success Message -->
                <div v-if="success || page.props.flash?.success" class="mb-8 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                        </svg>
                        {{ success || page.props.flash?.success }}
                    </div>
                </div>

                <!-- Error Messages -->
                <div v-if="(errors && Object.keys(errors).length > 0) || page.props.flash?.error" class="mb-8 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <div class="flex items-center mb-2">
                        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                        </svg>
                        <span class="font-semibold">Napaka pri pošiljanju sporočila:</span>
                    </div>
                    <div v-if="page.props.flash?.error" class="mb-2">
                        {{ page.props.flash.error }}
                    </div>
                    <ul v-if="errors && Object.keys(errors).length > 0" class="list-disc list-inside ml-6">
                        <li v-for="(error, field) in errors" :key="field">{{ error }}</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <!-- Contact Information -->
                    <div>
                        <h2 class="text-2xl font-semibold text-gray-900 mb-6">Stopite v stik</h2>
                        
                        <div class="space-y-6">
                            <div class="flex items-start space-x-4">
                                <div class="flex-shrink-0">
                                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900">Naslov</h3>
                                    <p class="text-gray-600">Ljubljana, Slovenija</p>
                                </div>
                            </div>

                            <div class="flex items-start space-x-4">
                                <div class="flex-shrink-0">
                                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900">Telefon</h3>
                                    <p class="text-gray-600">+386 51 659 656</p>
                                </div>
                            </div>

                            <div class="flex items-start space-x-4">
                                <div class="flex-shrink-0">
                                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900">E-pošta</h3>
                                    <p class="text-gray-600">info@lexo.si</p>
                                </div>
                            </div>

                            <div class="flex items-start space-x-4">
                                <div class="flex-shrink-0">
                                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-gray-900">Delovni čas</h3>
                                    <p class="text-gray-600">Ponedeljek - Petek: 9:00 - 18:00</p>
                                    <p class="text-gray-600">Sobota: Zaprto</p>
                                    <p class="text-gray-600">Nedelja in prazniki: Zaprto</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Contact Form -->
                    <div>
                        <h2 class="text-2xl font-semibold text-gray-900 mb-6">Pošljite nam sporočilo</h2>
                        
                        <form @submit.prevent="submitForm" class="space-y-6">
                            <div>
                                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">Ime in priimek</label>
                                <input 
                                    type="text" 
                                    id="name" 
                                    v-model="form.name"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    :class="{ 'border-red-500': errors && errors.name }"
                                    required
                                >
                                <p v-if="errors && errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
                            </div>

                            <div>
                                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">E-pošta</label>
                                <input 
                                    type="email" 
                                    id="email" 
                                    v-model="form.email"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    :class="{ 'border-red-500': errors && errors.email }"
                                    required
                                >
                                <p v-if="errors && errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
                            </div>

                            <div>
                                <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">Zadeva</label>
                                <input 
                                    type="text" 
                                    id="subject" 
                                    v-model="form.subject"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    :class="{ 'border-red-500': errors && errors.subject }"
                                    required
                                >
                                <p v-if="errors && errors.subject" class="mt-1 text-sm text-red-600">{{ errors.subject }}</p>
                            </div>

                            <div>
                                <label for="message" class="block text-sm font-medium text-gray-700 mb-2">Sporočilo</label>
                                <textarea 
                                    id="message" 
                                    v-model="form.message"
                                    rows="6"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    :class="{ 'border-red-500': errors && errors.message }"
                                    required
                                ></textarea>
                                <p v-if="errors && errors.message" class="mt-1 text-sm text-red-600">{{ errors.message }}</p>
                            </div>

                            <button 
                                type="submit"
                                :disabled="isSubmitting"
                                class="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span v-if="isSubmitting">Pošiljam...</span>
                                <span v-else>Pošlji sporočilo</span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </WebLayout>
</template>